/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 툴바 컴포넌트 클래스를 정의한다.
 *
 * @file /src/components/taskbar/Taskbar.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 8.
 */
import * as parent from '@base/Component';
import { Viewport } from '@base/Viewport';
import { Navigation } from '@components/navigation/Navigation';
import { Task } from './elements/Task';
import { Button } from '@components/button/Button';
import { Window } from '@components/window/Window';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { LanguageText } from '@utils/Language';
import { Renderer } from '@utils/Renderer';
import { Utils } from '@utils/Utils';
import './Taskbar.css';

export interface TaskProvider {
    icon: string;
    title: LanguageText;
    base: string;
}

export interface TaskProperty {
    icon: string;
    title: string;
    url: string;
    base: string;
    isLoaded: boolean;
    isActive: boolean;
    isFixed: boolean;
}

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 작업이 추가 또는 제거되거나 고정상태가 변경되었을 때
     */
    update?: (tasks: Task[], me: T) => Promise<void>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 툴바위치
     */
    position?: 'top' | 'bottom';

    /**
     * 내비게이션 - 시작메뉴로 표시된다.
     */
    navigation?: Navigation;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Taskbar extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'taskbar';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'taskbar';

    /**
     * 툴바위치
     */
    position: 'top' | 'bottom' = 'top';

    /**
     * 내비게이션
     */
    navigation: Navigation | null;

    /**
     * 시작버튼
     */
    $startButton: Dom = null;

    /**
     * 고정된 Taks 항목
     */
    fixedTasks: string[] = [];

    /**
     * 작업 목록 Dom
     */
    $tasks: Dom = null;

    /**
     * 툴 목록 Dom
     */
    $tools: Dom = null;

    /**
     * 작업표시줄 컴포넌트를 정의한다.
     *
     * @param properties - 컴포넌트 초기설정
     */
    constructor(public properties: Pick<Properties<Taskbar>, 'id' | 'position' | 'navigation' | 'listeners'> = null) {
        super(properties);

        this.scrollable = 'x';
        this.$scrollable = this.$getContent();
        this.position = this.properties.position ?? 'top';
        this.navigation = this.properties.navigation ?? null;
        this.layout = 'column';
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        this.items = [];
    }

    /**
     * 뷰포트를 지정한다.
     *
     * @param viewport - 뷰포트
     */
    setViewport(viewport: Viewport): void {
        super.setViewport(viewport);
        this.navigation?.setViewport(viewport);

        this.viewport.addEvent('active', async (panel) => {
            this.getTasks().forEach((task) => {
                if (task.id == panel.id) {
                    task.$getButton().addClass('active');
                }
            });
            this.storeTasks();
        });

        this.viewport.addEvent('deactive', async (panel) => {
            this.getTasks().forEach((task) => {
                if (task.id == panel.id) {
                    task.$getButton().removeClass('active');
                }
            });
            this.storeTasks();
        });

        this.viewport.addEvent('close', async (panel) => {
            this.getTasks().forEach(async (task) => {
                if (task.id == panel.id) {
                    const activated = task.isActive() == true;
                    await task.close();

                    if (activated == true) {
                        await this.getViewport().activeLatest(true);
                    }
                }
            });
            this.storeTasks();
        });
    }

    /**
     * 툴바위치를 정의한다.
     *
     * @param position - 툴바위치 (top, bottom)
     */
    setPosition(position: 'top' | 'bottom'): void {
        this.position = position;
    }

    /**
     * 내비게이션을 가져온다.
     *
     * @returns 내비게이션 (작업표시줄 또는 뷰포트에 정의된 내비게이션)
     */
    getNavigation(): Navigation | null {
        if (this.navigation !== null) {
            this.navigation.$getComponent().setAttr('popover', 'manual').setStyle('position-anchor', `--navigation`);
        }
        return this.navigation ?? this.getViewport().navigation ?? null;
    }

    /**
     * 컴포넌트 콘텐츠 DOM 을 가져온다.
     * 컴포넌트 종류에 따라 콘텐츠 DOM 타입변경이 필요할 경우, 하위 클래스에서 재정의한다.
     *
     * @returns 콘텐츠 DOM
     */
    $getContent(): Dom {
        if (this.$content === null) {
            this.$content = super.$getContent();
            this.$content.on('pointerleave', () => {
                Html.all('button.fixed.over', this.$getTasks()).removeClass('over');
            });
            this.$content.on('pointercancel', () => {
                Html.all('button.fixed.over', this.$getTasks()).removeClass('over');
            });
        }

        return this.$content;
    }

    /**
     * 시작버튼을 가져온다.
     *
     * @returns 시작버튼
     */
    $getStartButton(): Dom {
        if (this.$startButton === null) {
            this.$startButton = Html.create('button', { type: 'button', 'data-action': 'start' });
            this.$startButton.setStyle('anchor-name', `--navigation`);
            this.$startButton.on('pointerdown', async (_, e) => {
                if (this.navigation.isRendered() == false) {
                    Html.get('*[data-role=aui]').append(this.getNavigation().$getComponent());
                    await Renderer.request(() => this.getNavigation().render(true));
                }
                if (this.getNavigation().$getComponent().getEl().matches(':popover-open') === true) {
                    this.getNavigation().$getComponent().getEl().hidePopover();
                } else {
                    this.getNavigation().$getComponent().getEl().showPopover();
                }
                e.stopImmediatePropagation();
            });
        }

        return this.$startButton;
    }

    /**
     * 작업 목록 Dom을 가져온다.
     *
     * @returns 작업 목록 Dom
     */
    $getTasks(): Dom {
        if (this.$tasks === null) {
            this.$tasks = Html.create('ul');
        }

        return this.$tasks;
    }

    /**
     * 툴 목록 Dom을 가져온다.
     *
     * @returns 툴 목록 Dom
     */
    $getTools(): Dom {
        if (this.$tools === null) {
            this.$tools = Html.create('div', { 'data-role': 'tools' });
        }

        return this.$tools;
    }

    /**
     * 전체 작업을 가져온다.
     *
     * @returns 전체 작업
     */
    getTasks(): Task[] {
        const tasks: Task[] = [];
        Html.all('li:not(.drag)', this.$getTasks()).forEach(($task) => {
            tasks.push($task.getData('task'));
        });

        return tasks;
    }

    /**
     * URL 에 해당하는 작업이 존재하는지 검색한다.
     *
     * @param url - 검색할 URL
     * @returns 검색결과
     */
    async getTasksByUrl(url: string): Promise<Task[]> {
        const tasks = this.getTasks();
        const results: Task[] = [];
        for (const task of tasks) {
            const current = await task.getUrl(true);
            if (current?.search(new RegExp('^' + url)) === 0) {
                results.push(task);
            }
        }

        return results;
    }

    /**
     * 작업아이콘 작업을 추가한다.
     *
     * @param icon - 아이콘
     * @param title - 타이틀
     * @param base - 작업 기본 URL
     * @param fixed - 고정여부
     * @returns 추가된 작업아이콘
     */
    async addIcon(icon: string, title: LanguageText, base: string, fixed: boolean = true): Promise<Task> {
        /*
         * 이미 작업이 열려져 있다면, 해당 작업의 상태를 변경하고 아이콘을 추가하지 않는다.
         */
        const current = await this.getTasksByUrl(base);
        if (current.length == 0 || fixed == false) {
            const provider: TaskProvider = { icon, title, base };
            const task = new Task(provider);
            task.setComponent(this);
            if (fixed == true) {
                await task.setFixed(true, false);
            }
            this.$getTasks().append(task.$getElement());
            await task.render();

            return task;
        } else {
            if (current[0].isFixed() != fixed) {
                await current[0].setFixed(fixed, false);
            }

            return current[0];
        }
    }

    /**
     * 작업을 추가한다.
     *
     * @param target - 추가할 작업대상
     * @returns 작업
     */
    async addTask(task: Task): Promise<Task> {
        task.setComponent(this);
        this.$getTasks().append(task.$getElement());
        await task.render();

        this.storeTasks();

        /*
        if (target instanceof Window) {
            Html.all('button[data-type=window]', this.$getTasks()).removeClass('active');
        } else {
            Html.all('button:not([data-type=window])', this.$getTasks()).removeClass('active');
        }

        task.$getButton().addClass('active');

        this.storeTasks();
        */
        return task;
    }

    /**
     * 새로운 창 작업을 추가한다.
     *
     * @param window - 추가할 윈도우
     * @returns 작업
     */
    async addWindow(window: Window): Promise<Task> {
        const task = new Task(window);
        return await this.addTask(task);
    }

    /**
     * 툴을 추가한다.
     *
     * @param tool - 툴 버튼
     */
    addTool(tool: Button) {
        this.$getTools().append(tool.$getComponent());
        tool.setParent(this);
        tool.render(true);

        if (this.isRendered() == true) {
            this.getScroller().updatePosition();
        }
    }

    /**
     * 작업 목록의 현재 상태를 저장한다.
     */
    async storeTasks(): Promise<void> {
        if (this.isRendered() == false) {
            return;
        }

        const tasks = this.getTasks();
        const items: TaskProperty[] = [];

        for (const task of tasks) {
            if (task.hasProvider() == true) {
                items.push({
                    icon: task.getIcon(),
                    title: await task.getText(),
                    base: await task.getUrl(true),
                    url: await task.getUrl(),
                    isLoaded: task.hasTask(),
                    isActive: task.isActive(),
                    isFixed: task.isFixed(),
                });
            }
        }

        Utils.session('tasks', items);
        await this.fireEvent('update', [tasks, this]);
    }

    /**
     * 저장된 작업을 복원한다.
     *
     * @returns 저장된 활성작업
     */
    async restoreTasks(): Promise<Task | null> {
        const items: TaskProperty[] = Utils.session('tasks') ?? [];

        let activated: Task = null;
        for (const item of items) {
            const task = await this.addIcon(item.icon, item.title, item.base, item.isFixed);
            task.setUrl(item.url);

            if (item.isLoaded == true) {
                task.setTask(true);
            }
            if (task.isFixed() == true) {
                await task.setFixed(true, false);
            }
            if (item.isActive == true) {
                activated = task;
            }
        }

        await Utils.sleep(200);

        this.getScroller().updatePosition();

        return activated;
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        if (this.$getContainer().isConnected() == false) {
            this.$getComponent().append(this.$getContainer());
        }

        this.$getContainer().addClass(this.position);
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        if (this.navigation !== null) {
            this.$getContainer().append(this.$getStartButton());
        }

        this.$getContent().append(this.$getTasks());
        this.$getContainer().append(this.$getContent());
        this.$getContainer().append(this.$getTools());
    }
}
